<template>
  <div class="model-main-page">
    <!-- 顶部边界 -->
    <div class="top-border">
      <div class="corner-text">
        <i class="fas fa-city"></i>
        武汉市桥隧群数字孪生系统
      </div>
      <div class="top-right">
        <button class="top-btn" @click="$router.push({ name: 'RoadNet' })">
          <i class="fas fa-home"></i>
          回到首页
        </button>
      </div>
    </div>

    <!-- 左侧边栏 -->
    <div class="left-border">
      <div class="sidebar-title">
        <i class="fas fa-bridge"></i> 桥梁选择
      </div>
      <div class="button-container">
        <button
          v-for="bridge in bridges"
          :key="bridge.id"
          class="sidebar-button"
          :class="{ active: currentBridgeId === bridge.id }"
          @click="switchBridge(bridge.id)"
        >
          <i :class="bridge.icon"></i> {{ bridge.name }}
        </button>
      </div>
    </div>

    <!-- 内容区域：Three.js 场景容器 -->
    <div class="content-area">
      <div class="bridge-title">
        <i :class="currentBridgeIcon"></i>
        {{ currentBridgeName }} - 3D模型
      </div>
      <div class="model-container" ref="modelContainer"></div>
      <div class="watermark">3D MODEL</div>
    </div>

    <!-- 加载动画 -->
    <div v-if="showLoading" id="load">
      <div class="load-container">
        <div class="load-img-wrapper">
          <div class="circle-outer"></div>
          <div class="circle-inner"></div>
        </div>
        <div class="load-center">加载中</div>
      </div>
      <div class="loading-text">数字孪生系统初始化</div>
      <div class="loading-subtext">正在加载三维模型与传感器数据...</div>
    </div>

    <!-- 底部信息栏 -->
    <div class="info-bar">
      <div class="system-status">
        <span class="status-indicator"></span>
        <span class="status-text">系统状态: <strong>运行正常</strong></span>
      </div>
      <div class="datetime">
        <i class="far fa-clock"></i>
        <span>{{ datetimeText }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export default {
  name: 'ModelMainWuhan',
  data() {
    return {
      // 桥梁配置：包含模型路径、相机参数、视角目标等（完全遵照原始js文件）
      bridges: [
        { 
          id: 'whcj', 
          name: '武汉长江大桥', 
          icon: 'fas fa-bridge-water',
          modelUrl: 'https://cdn.bridgetunnelgroup.site/models/whcj.glb',
          cameraPos: [-2851, 1201, 1113],
          targetPos: [-2459, 1070, 672]
        },
        { 
          id: 'whcj2', 
          name: '武汉长江二桥', 
          icon: 'fas fa-archway',
          modelUrl: 'https://cdn.bridgetunnelgroup.site/models/whcj2.glb',
          cameraPos: [-1763, 1204, 785],
          targetPos: [-1176, 1060, 289]
        },
        { 
          id: 'ysg', 
          name: '杨泗港长江大桥', 
          icon: 'fas fa-road-bridge',
          modelUrl: 'https://cdn.bridgetunnelgroup.site/models/ysg.glb',
          cameraPos: [-2745, 1235, 2189],
          targetPos: [-1534, 1025, 623]
        },
        { 
          id: 'yl', 
          name: '阳逻长江大桥', 
          icon: 'fas fa-draw-polygon',
          modelUrl: 'https://cdn.bridgetunnelgroup.site/models/yl.glb',
          cameraPos: [-2490, 1166, 1699],
          targetPos: [-2098, 1117, 1201]
        },
        { 
          id: 'txz', 
          name: '天兴洲长江大桥', 
          icon: 'fas fa-bridge',
          modelUrl: 'https://cdn.bridgetunnelgroup.site/models/txz.glb',
          cameraPos: [-3446, 1209, 2712],
          targetPos: [-2961, 1045, 1900]
        },
        { 
          id: 'bsz', 
          name: '白沙洲长江大桥', 
          icon: 'fas fa-bridge-circle-check',
          modelUrl: 'https://cdn.bridgetunnelgroup.site/models/bsz.glb',
          cameraPos: [-1914, 1171, 975],
          targetPos: [-1665, 1114, 632]
        },
        { 
          id: 'eq', 
          name: '二七长江大桥', 
          icon: 'fas fa-bridge-circle-exclamation',
          modelUrl: 'https://cdn.bridgetunnelgroup.site/models/eq.glb',
          cameraPos: [-1753, 1219, 1527],
          targetPos: [-1215, 1089, 862]
        },
        { 
          id: 'qs', 
          name: '青山长江大桥', 
          icon: 'fas fa-bridge-lock',
          modelUrl: 'https://cdn.bridgetunnelgroup.site/models/qs.glb',
          cameraPos: [-4404, 1211, 2478],
          targetPos: [-3971, 1114, 1835]
        }
      ],
      currentBridgeId: 'whcj',
      showLoading: true,
      datetimeText: '',

      // Three.js 核心对象
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      modelGroup: null,
      animationId: null,
      resizeHandler: null,
      datetimeTimer: null,
      loader: null,
      raycaster: null,
      mouseVector: null,
      // 高亮定时器
      highlightTimeout: null
    };
  },
  computed: {
    currentBridgeName() {
      const bridge = this.bridges.find(b => b.id === this.currentBridgeId);
      return bridge ? bridge.name : '武汉长江大桥';
    },
    currentBridgeIcon() {
      const bridge = this.bridges.find(b => b.id === this.currentBridgeId);
      return bridge ? bridge.icon : 'fas fa-bridge-water';
    },
    currentBridgeConfig() {
      return this.bridges.find(b => b.id === this.currentBridgeId);
    }
  },
  mounted() {
    this.initDateTime();
    this.initThree();
    // 应用当前桥梁的相机/视角配置
    this.applyBridgeConfig(this.currentBridgeId);
    this.loadBridgeModel(this.currentBridgeId);
  },
  beforeDestroy() {
    this.cleanupThree();
  },
  methods: {
    goBack() {
      if (this.$router) {
        this.$router.push({ name: 'Home' });
      } else {
        window.history.back();
      }
    },

    initDateTime() {
      const update = () => {
        const now = new Date();
        const options = {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        };
        this.datetimeText = now.toLocaleDateString('zh-CN', options);
      };
      update();
      this.datetimeTimer = setInterval(update, 1000);
    },

    // 应用桥梁的相机位置、控制目标、背景色（完全按照原始js风格）
    applyBridgeConfig(bridgeId) {
      const config = this.bridges.find(b => b.id === bridgeId);
      if (!config || !this.camera || !this.controls) return;

      // 设置相机位置和视角目标
      this.camera.position.set(config.cameraPos[0], config.cameraPos[1], config.cameraPos[2]);
      this.controls.target.set(config.targetPos[0], config.targetPos[1], config.targetPos[2]);
      this.controls.update();

      // 背景色：原始js均为 #87CEEB (天空蓝)
      if (this.scene) {
        this.scene.background = new THREE.Color('#87CEEB');
        // 移除雾效（原始js无雾）
        this.scene.fog = null;
      }
    },

    // 初始化 Three.js 场景（灯光、渲染器、轨道控制、点击交互）
    initThree() {
      const container = this.$refs.modelContainer;
      if (!container) return;

      const width = container.clientWidth;
      const height = container.clientHeight;

      // 场景 (背景色稍后由桥梁配置设置)
      const scene = new THREE.Scene();
      scene.background = new THREE.Color('#87CEEB');

      // 模型组（用于切换时清空）
      const modelGroup = new THREE.Group();
      scene.add(modelGroup);

      // 辅助坐标轴（默认隐藏）
      const axesHelper = new THREE.AxesHelper(200);
      axesHelper.visible = false;
      scene.add(axesHelper);

      // 灯光：完全遵照原始js —— 方向光 + 环境光
      // 方向光强度3，位置 (-500,300,342)
      const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
      directionalLight.position.set(-500, 300, 342);
      directionalLight.castShadow = true;
      directionalLight.receiveShadow = false;
      scene.add(directionalLight);
      
      // 环境光强度0.1
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.1);
      scene.add(ambientLight);

      // 相机 (透视相机，fov=30，与原始js一致)
      const camera = new THREE.PerspectiveCamera(30, width / height, 1, 10000);
      // 临时位置，稍后会被 applyBridgeConfig 覆盖
      camera.position.set(0, 0, 0);

      // 渲染器
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.shadowMap.enabled = true;
      // 兼容旧版Three.js的颜色编码设置
      if (renderer.outputEncoding !== undefined && THREE.sRGBEncoding !== undefined) {
        renderer.outputEncoding = THREE.sRGBEncoding;
      } else if (renderer.outputColorSpace !== undefined) {
        renderer.outputColorSpace = THREE.SRGBColorSpace;
      }
      container.appendChild(renderer.domElement);

      // 轨道控制
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.rotateSpeed = 1.0;
      controls.zoomSpeed = 1.2;
      controls.panSpeed = 0.8;

      // 动画循环
      const animate = () => {
        this.animationId = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      // 窗口自适应
      const onResize = () => {
        const w = container.clientWidth;
        const h = container.clientHeight;
        renderer.setSize(w, h);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      };
      window.addEventListener('resize', onResize);

      // 初始化射线检测器（用于模型点击高亮）
      const raycaster = new THREE.Raycaster();
      const mouseVector = new THREE.Vector2();

      // 绑定点击事件
      renderer.domElement.addEventListener('click', (event) => {
        if (!this.scene || !this.camera) return;
        
        // 计算鼠标位置归一化坐标
        const rect = renderer.domElement.getBoundingClientRect();
        mouseVector.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouseVector.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        
        raycaster.setFromCamera(mouseVector, this.camera);
        
        // 收集所有可点击的网格（具有 userData.clickable 标记）
        const clickableObjects = [];
        this.scene.traverse(child => {
          if (child.isMesh && child.userData.clickable) {
            clickableObjects.push(child);
          }
        });
        
        const intersects = raycaster.intersectObjects(clickableObjects);
        if (intersects.length > 0) {
          const clickedMesh = intersects[0].object;
          console.log('点击模型部件:', clickedMesh.name || '未命名');
          
          // 高亮效果：改变材质颜色为红色，0.5秒后恢复
          if (clickedMesh.material) {
            // 保存原始颜色（如果是数组材质，只处理第一个）
            const originalColor = clickedMesh.material.color ? clickedMesh.material.color.getHex() : null;
            if (originalColor !== null) {
              clickedMesh.material.color.setHex(0xff3300);
              if (this.highlightTimeout) clearTimeout(this.highlightTimeout);
              this.highlightTimeout = setTimeout(() => {
                if (clickedMesh.material) {
                  clickedMesh.material.color.setHex(originalColor);
                }
              }, 500);
            }
          }
        }
      });

      // 保存实例
      this.scene = scene;
      this.camera = camera;
      this.renderer = renderer;
      this.controls = controls;
      this.modelGroup = modelGroup;
      this.resizeHandler = onResize;
      this.loader = new GLTFLoader();
      this.raycaster = raycaster;
      this.mouseVector = mouseVector;
    },

    // 切换桥梁
    switchBridge(bridgeId) {
      if (this.currentBridgeId === bridgeId) return;
      this.currentBridgeId = bridgeId;
      // 立即更新相机视角和背景
      this.applyBridgeConfig(bridgeId);
      // 清空旧模型并加载新模型
      this.clearModelGroup();
      this.loadBridgeModel(bridgeId);
    },

    // 加载指定桥梁的 glb 模型
    loadBridgeModel(bridgeId) {
      if (!this.scene || !this.modelGroup || !this.loader) return;

      this.showLoading = true;

      const config = this.bridges.find(b => b.id === bridgeId);
      if (!config || !config.modelUrl) {
        console.error(`未找到桥梁 ${bridgeId} 的模型配置`);
        this.showLoading = false;
        return;
      }

      this.loader.load(
        config.modelUrl,
        (gltf) => {
          const model = gltf.scene;
          // 不进行额外位移，保持原始模型坐标系
          model.position.set(0, 0, 0);
          // 遍历所有网格，开启阴影并添加可点击标识
          model.traverse((child) => {
            if (child.isMesh) {
              child.castShadow = true;
              child.receiveShadow = false;
              child.userData.clickable = true;
            }
          });
          this.modelGroup.add(model);
          // 确保加载完成后视角配置仍然生效（防止相机被意外改动）
          this.applyBridgeConfig(bridgeId);
          setTimeout(() => {
            this.showLoading = false;
          }, 200);
        },
        () => {
          // 进度回调（无需处理）
        },
        (error) => {
          console.error(`模型加载失败: ${config.modelUrl}`, error);
          this.showLoading = false;
          // 可选：提示用户
        }
      );
    },

    // 清空模型组并释放内存
    clearModelGroup() {
      if (!this.modelGroup) return;
      while (this.modelGroup.children.length) {
        const child = this.modelGroup.children.pop();
        if (!child) break;
        child.traverse((obj) => {
          if (obj.isMesh) {
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) {
              if (Array.isArray(obj.material)) {
                obj.material.forEach(m => m.dispose());
              } else {
                obj.material.dispose();
              }
            }
          }
        });
        this.scene.remove(child);
      }
    },

    // 完全清理 Three.js 资源
    cleanupThree() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }
      if (this.datetimeTimer) {
        clearInterval(this.datetimeTimer);
        this.datetimeTimer = null;
      }
      if (this.highlightTimeout) {
        clearTimeout(this.highlightTimeout);
        this.highlightTimeout = null;
      }
      if (this.renderer) {
        this.renderer.dispose();
        if (this.renderer.domElement && this.renderer.domElement.parentNode) {
          this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
        this.renderer = null;
      }
      if (this.controls) {
        this.controls.dispose();
        this.controls = null;
      }
      if (this.scene) {
        this.clearModelGroup();
        this.scene.clear();
        this.scene = null;
      }
      this.loader = null;
      this.raycaster = null;
      this.mouseVector = null;
    }
  }
};
</script>

<style scoped>
.model-main-page {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #0a1429, #1d365f);
  color: #e6f7ff;
  font-family: 'Microsoft YaHei', 'Segoe UI', sans-serif;
}

/* 顶部边界 - 玻璃拟态 */
.top-border {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 70px;
  background: rgba(10, 25, 50, 0.75);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 30px;
  box-sizing: border-box;
  z-index: 1000;
  border-bottom: 1px solid rgba(100, 150, 255, 0.15);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.corner-text {
  color: #4dc2ff;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-shadow: 0 0 10px rgba(77, 194, 255, 0.5);
  display: flex;
  align-items: center;
  gap: 12px;
}

.corner-text i {
  font-size: 26px;
}

.top-right {
  display: flex;
  align-items: center;
}

.top-btn {
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 40px;
  padding: 8px 20px;
  color: #e5f0ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.top-btn i {
  font-size: 16px;
}

.top-btn:hover {
  background: rgba(37, 99, 235, 0.95);
  color: #ffffff;
  border-color: rgba(191, 219, 254, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.3);
}

/* 左侧边栏 */
.left-border {
  position: absolute;
  top: 70px;
  left: 0;
  width: 240px;
  bottom: 40px;
  background: rgba(15, 30, 60, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 900;
  display: flex;
  flex-direction: column;
  padding: 25px 0;
  box-sizing: border-box;
  border-right: 1px solid rgba(100, 150, 255, 0.15);
  box-shadow: 5px 0 15px rgba(0, 0, 0, 0.2);
  overflow-y: auto;
}

.sidebar-title {
  background: linear-gradient(135deg, #2a75d1, #00c6ff);
  color: white;
  text-align: center;
  width: 85%;
  padding: 15px 0;
  font-size: 19px;
  font-weight: bold;
  border-radius: 12px;
  margin: 10px auto 35px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease;
}

.sidebar-title i {
  margin-right: 8px;
}

.button-container {
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 85%;
  margin: 0 auto;
  padding: 10px 0;
}

.sidebar-button {
  background: linear-gradient(135deg, rgba(40, 60, 100, 0.8), rgba(25, 45, 85, 0.9));
  color: #d6eeff;
  border: none;
  border-radius: 10px;
  padding: 14px 0;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  letter-spacing: 1px;
}

.sidebar-button i {
  font-size: 20px;
}

.sidebar-button:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
  color: #ffffff;
}

.sidebar-button.active {
  background: linear-gradient(135deg, #00c6ff, #2a75d1);
  color: #0c1b33;
  box-shadow: 0 0 20px rgba(0, 198, 255, 0.6);
  transform: translateY(-2px);
}

/* 内容区域 */
.content-area {
  position: absolute;
  top: 70px;
  left: 240px;
  right: 0;
  bottom: 40px;
  overflow: hidden;
  z-index: 800;
  background: rgba(10, 20, 40, 0.3);
}

.model-container {
  width: 100%;
  height: 100%;
  position: relative;
  outline: none;
}

.bridge-title {
  position: absolute;
  top: 25px;
  left: 25px;
  z-index: 1000;
  background: rgba(0, 20, 50, 0.75);
  backdrop-filter: blur(5px);
  padding: 10px 24px;
  border-radius: 40px;
  font-size: 20px;
  font-weight: 700;
  color: #4dc2ff;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(100, 180, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 10px;
  pointer-events: none;
}

.bridge-title i {
  font-size: 24px;
}

.watermark {
  position: absolute;
  bottom: 60px;
  right: 25px;
  color: rgba(100, 180, 255, 0.12);
  font-size: 100px;
  font-weight: 900;
  z-index: 5;
  pointer-events: none;
  transform: rotate(-15deg);
  user-select: none;
  font-family: Arial, sans-serif;
}

/* 加载动画 */
#load {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0a1429, #1a325e);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
}

.load-container {
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.load-img-wrapper {
  position: absolute;
  width: 100%;
  height: 100%;
}

.circle-outer,
.circle-inner {
  position: absolute;
  border-radius: 50%;
  border: 3px solid rgba(0, 198, 255, 0.7);
  border-top-color: transparent;
  border-right-color: transparent;
  animation: rotate 2s linear infinite;
}

.circle-outer {
  width: 200px;
  height: 200px;
}

.circle-inner {
  width: 140px;
  height: 140px;
  top: 30px;
  left: 30px;
  animation-duration: 1.5s;
  border-width: 3px;
  border-color: rgba(77, 194, 255, 0.6);
  border-top-color: transparent;
}

@keyframes rotate {
  to {
    transform: rotate(360deg);
  }
}

.load-center {
  position: absolute;
  width: 80px;
  height: 80px;
  background: rgba(10, 25, 50, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #4dc2ff;
  z-index: 10;
  border: 2px solid rgba(100, 180, 255, 0.3);
  backdrop-filter: blur(2px);
}

.loading-text {
  font-size: 22px;
  color: #4dc2ff;
  text-align: center;
  text-shadow: 0 0 10px rgba(77, 194, 255, 0.7);
  margin-top: 30px;
  font-weight: 600;
  letter-spacing: 1px;
}

.loading-subtext {
  font-size: 16px;
  color: #88bbff;
  text-align: center;
  margin-top: 15px;
  opacity: 0.8;
}

/* 底部信息栏 */
.info-bar {
  position: absolute;
  bottom: 0;
  left: 240px;
  right: 0;
  height: 40px;
  background: rgba(10, 25, 50, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  padding: 0 25px;
  font-size: 14px;
  color: #aad5ff;
  z-index: 900;
  border-top: 1px solid rgba(100, 150, 255, 0.15);
  box-sizing: border-box;
}

.system-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-indicator {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #00ff9d;
  box-shadow: 0 0 8px #00ff9d;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 255, 157, 0.6);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(0, 255, 157, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 255, 157, 0);
  }
}

.datetime {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 响应式适配 */
@media (max-width: 1200px) {
  .left-border {
    width: 210px;
  }
  .content-area {
    left: 210px;
  }
  .info-bar {
    left: 210px;
  }
  .sidebar-button {
    font-size: 14px;
    padding: 12px 0;
  }
  .bridge-title {
    font-size: 18px;
  }
}
</style>